/**
 * Super-admin v2 — CreateCafeWizard (3 steps + success screen).
 * Step 1: cafe info (name + city)
 * Step 2: owner (name, email, phone, password)
 * Step 3: plan (Basic / Standard / Premium) + trial duration
 * Success screen: credentials summary + send/copy
 */
import { useEffect, useState } from "react";
import { createTenant, lookupOwnerCafes, type OwnerCafeRow } from "../hooks/useTenants";
import { superAdminCrmConvertLead } from "@cafe-os/db";
import { C, FONT, inputStyle, btnPrimary, btnSecondary } from "../styles/theme";
import { ModalWrapper, FormField } from "./ui";
import { Store, User, Globe, Gem, Dices, Clipboard, ExternalLink, Check, CheckCircle2, AlertTriangle, Lightbulb } from "lucide-react";

export interface WizardPrefill {
  cafeName?: string;
  city?: string;
  ownerName?: string;
  ownerPhone?: string;
  ownerEmail?: string;
  /** Если задан — после успешного create вызовется superAdminCrmConvertLead. */
  leadId?: string;
}

interface Props {
  onClose: () => void;
  onCreated?: () => void;
  /** Префилл из CRM-лида + опциональный leadId для авто-конверсии. */
  prefill?: WizardPrefill;
}

type Step = 1 | 2 | 3 | 4;
type PlanId = "starter" | "pro" | "enterprise";

interface WizardData {
  cafeName: string;
  city: string;
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  password: string;
  plan: PlanId;
  trialDays: 7 | 14 | 30;
}

// Цены в USD с 2026-06-10 (расходы платформы в долларах) — синхронно
// с таблицей plans и лендингом e-menu.bar.
const PLANS: { id: PlanId; label: string; price: number; features: string[]; recommend?: boolean }[] = [
  { id: "starter",    label: "Starter",    price: 25, features: ["До 10 столов", "5 сотрудников", "Базовая аналитика"] },
  { id: "pro",        label: "Pro",        price: 60, features: ["До 50 столов", "20 сотрудников", "AI + доставка"], recommend: true },
  { id: "enterprise", label: "Enterprise", price: 90, features: ["Без лимитов", "White-label", "Приоритет"] },
];

function genPassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let pw = "";
  for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)];
  return pw;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}

export function CreateCafeWizard({ onClose, onCreated, prefill }: Props) {
  const [step, setStep] = useState<Step>(1);
  const [data, setData] = useState<WizardData>({
    cafeName:    prefill?.cafeName    ?? "",
    city:        prefill?.city        ?? "",
    ownerName:   prefill?.ownerName   ?? "",
    ownerEmail:  prefill?.ownerEmail  ?? "",
    ownerPhone:  prefill?.ownerPhone  ?? "",
    password: "", plan: "pro", trialDays: 14,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Сеть владельца (00163/00164): по email ищем его существующие кафе ──
  // и предлагаем привязку нового кафе к сети вместо ручного SQL.
  const [ownerCafes, setOwnerCafes] = useState<OwnerCafeRow[]>([]);
  const [networkChoice, setNetworkChoice] = useState<"none" | "existing" | "new">("none");
  const [newOrgName, setNewOrgName] = useState("");
  const [attachExisting, setAttachExisting] = useState(true);

  const existingOrg = ownerCafes.find((c) => c.orgId);
  // одиночные кафе владельца (без сети) — кандидаты на объединение в новую сеть
  const soloCafes = ownerCafes.filter((c) => !c.orgId);

  useEffect(() => {
    const email = data.ownerEmail.trim();
    if (!email.includes("@")) {
      setOwnerCafes([]);
      setNetworkChoice("none");
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      lookupOwnerCafes(email)
        .then((rows) => {
          if (cancelled) return;
          setOwnerCafes(rows);
          if (rows.length === 0) setNetworkChoice("none");
        })
        .catch(() => { /* lookup — подсказка, не блокер */ });
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [data.ownerEmail]);
  const [submitResult, setSubmitResult] = useState<{
    tenantSlug: string;
    ownerSetupUrl: string;
    vercelDomain?: "auto-added" | "manual-add-required";
    vercelError?: string | null;
    leadConverted?: boolean;
    leadConvertError?: string;
    orgId?: string | null;
    orgError?: string | null;
    planId?: string | null;
    planError?: string | null;
  } | null>(null);

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const slug = slugify(data.cafeName);
      const res = await createTenant({
        slug,
        nameRu: data.cafeName,
        nameKz: data.cafeName,
        nameEn: data.cafeName,
        ownerEmail: data.ownerEmail.toLowerCase(),
        ownerName: data.ownerName,
        city: data.city,
        orgId: networkChoice === "existing" ? existingOrg?.orgId ?? undefined : undefined,
        newOrgName: networkChoice === "new" ? newOrgName.trim() || data.ownerName : undefined,
        attachExistingTenantIds:
          networkChoice === "new" && attachExisting && soloCafes.length > 0
            ? soloCafes.map((c) => c.tenantId)
            : undefined,
        // SA-1: тариф/trial из шага 3 раньше молча выбрасывались
        planId: data.plan,
        trialDays: data.trialDays,
      });

      // Lead → Tenant conversion (если wizard открыт из CRM с leadId).
      let leadConverted: boolean | undefined;
      let leadConvertError: string | undefined;
      if (prefill?.leadId) {
        try {
          await superAdminCrmConvertLead(prefill.leadId, res.tenantSlug);
          leadConverted = true;
        } catch (e) {
          leadConvertError = e instanceof Error ? e.message : String(e);
          // Tenant создан успешно, но конверсия упала — не блокируем UX, показываем warning.
        }
      }

      setSubmitResult({
        tenantSlug: res.tenantSlug,
        ownerSetupUrl: res.ownerSetupUrl,
        vercelDomain: res.vercelDomain,
        vercelError: res.vercelError,
        leadConverted,
        leadConvertError,
        orgId: res.orgId,
        orgError: res.orgError,
        planId: res.planId,
        planError: res.planError,
      });
      setStep(4);
      onCreated?.();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalWrapper onClose={onClose}>
      <div style={{ padding: 32 }}>
        {/* Stepper */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
          {([1, 2, 3] as Step[]).map((s) => (
            <div key={s} style={{ display: "flex", alignItems: "center", gap: 8, flex: s < 3 ? 1 : "auto" }}>
              <div style={{
                width: 32, height: 32, borderRadius: "50%",
                background: step >= s ? C.brand : C.bgSoft,
                color: step >= s ? C.onBrand : C.muted,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 800, fontFamily: FONT.display,
                transition: "all 0.3s",
              }}>{step > s ? <Check size={16} /> : s}</div>
              {s < 3 && (
                <div style={{
                  flex: 1, height: 2,
                  background: step > s ? C.brand : C.border,
                }} />
              )}
            </div>
          ))}
        </div>

        {step === 1 && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: FONT.display, fontSize: 24, fontWeight: 700, marginBottom: 8, color: C.text }}>
              Создать новое кафе <Store size={22} />
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>
              Шаг 1 из 3 — информация о кафе
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <FormField label="Название кафе *">
                <input type="text" value={data.cafeName}
                  onChange={(e) => setData({ ...data, cafeName: e.target.value })}
                  placeholder="Café Әлем" style={inputStyle()} />
              </FormField>
              <FormField label="Город *">
                <input type="text" value={data.city}
                  onChange={(e) => setData({ ...data, city: e.target.value })}
                  placeholder="Шымкент" style={inputStyle()} />
              </FormField>
              {data.cafeName && (
                <div style={{
                  fontSize: 11, color: C.muted, padding: "8px 12px",
                  background: C.bgSoft, borderRadius: 8, fontFamily: FONT.mono,
                }}>
                  Слаг: <strong style={{ color: C.brand }}>{slugify(data.cafeName)}</strong> · URL:{" "}
                  <strong style={{ color: C.brand }}>{slugify(data.cafeName)}.e-menu.bar</strong>
                </div>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 32 }}>
              <button onClick={onClose} style={btnSecondary()}>Отмена</button>
              <button disabled={!data.cafeName || !data.city}
                onClick={() => setStep(2)}
                style={btnPrimary(!data.cafeName || !data.city)}>
                Далее →
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: FONT.display, fontSize: 24, fontWeight: 700, marginBottom: 8, color: C.text }}>
              Владелец кафе <User size={22} />
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>
              Шаг 2 из 3 — данные для входа
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <FormField label="Имя владельца *">
                <input type="text" value={data.ownerName}
                  onChange={(e) => setData({ ...data, ownerName: e.target.value })}
                  placeholder="Алмас Жанибеков" style={inputStyle()} />
              </FormField>
              <FormField label="Email (для входа) *">
                <input type="email" value={data.ownerEmail}
                  onChange={(e) => setData({ ...data, ownerEmail: e.target.value })}
                  placeholder="almas@bistro.kz" style={inputStyle()} />
              </FormField>

              {/* Сеть владельца: у этого email уже есть кафе (00164 lookup) */}
              {ownerCafes.length > 0 && (
                <div style={{
                  background: C.brandSoft, border: `1px solid ${C.brand}`,
                  borderRadius: 12, padding: 14,
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 12, fontWeight: 800, color: C.brand, marginBottom: 8 }}>
                    <Globe size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>
                      У этого владельца уже есть {ownerCafes.length === 1 ? "кафе" : "кафе"}:{" "}
                      {ownerCafes.map((c) => c.tenantName.ru || c.tenantId).join(", ")}
                      {existingOrg ? ` (сеть «${existingOrg.orgName?.ru || existingOrg.orgId}»)` : ""}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {existingOrg && (
                      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: C.text, cursor: "pointer" }}>
                        <input type="radio" name="networkChoice"
                          checked={networkChoice === "existing"}
                          onChange={() => setNetworkChoice("existing")} />
                        Добавить в сеть «{existingOrg.orgName?.ru || existingOrg.orgId}» — новое кафе появится на экране «Сеть»
                      </label>
                    )}
                    {!existingOrg && (
                      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: C.text, cursor: "pointer" }}>
                        <input type="radio" name="networkChoice"
                          checked={networkChoice === "new"}
                          onChange={() => setNetworkChoice("new")} />
                        Создать сеть и объединить кафе владельца
                      </label>
                    )}
                    <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: C.text, cursor: "pointer" }}>
                      <input type="radio" name="networkChoice"
                        checked={networkChoice === "none"}
                        onChange={() => setNetworkChoice("none")} />
                      Одиночное кафе (без сети)
                    </label>
                  </div>
                  {networkChoice === "new" && (
                    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                      <input type="text" value={newOrgName}
                        onChange={(e) => setNewOrgName(e.target.value)}
                        placeholder={`Название сети (по умолчанию: ${data.ownerName || "имя владельца"})`}
                        style={inputStyle()} />
                      {soloCafes.length > 0 && (
                        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: C.muted, cursor: "pointer" }}>
                          <input type="checkbox" checked={attachExisting}
                            onChange={(e) => setAttachExisting(e.target.checked)} />
                          Включить в сеть и существующие: {soloCafes.map((c) => c.tenantName.ru || c.tenantId).join(", ")}
                        </label>
                      )}
                    </div>
                  )}
                </div>
              )}
              <FormField label="Телефон">
                <input type="tel" value={data.ownerPhone}
                  onChange={(e) => setData({ ...data, ownerPhone: e.target.value })}
                  placeholder="+7 701 234 56 78" style={inputStyle()} />
              </FormField>
              <FormField label="Стартовый пароль (опц.)">
                <div style={{ display: "flex", gap: 8 }}>
                  <input type="text" value={data.password}
                    onChange={(e) => setData({ ...data, password: e.target.value })}
                    placeholder="Можно сгенерировать"
                    style={{ ...inputStyle(), fontFamily: FONT.mono }} />
                  <button onClick={() => setData({ ...data, password: genPassword() })} style={{
                    display: "flex", alignItems: "center", gap: 6,
                    background: C.brandSoft, color: C.brand, border: `1px solid ${C.brand}`,
                    padding: "0 16px", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer",
                  }}><Dices size={14} /> Создать</button>
                </div>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 11, color: C.muted, marginTop: 6 }}>
                  <Lightbulb size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>Текущий flow — magic-link на email. Пароль зарезервирован для будущего email/password входа.</span>
                </div>
              </FormField>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 32 }}>
              <button onClick={() => setStep(1)} style={btnSecondary()}>← Назад</button>
              <button disabled={!data.ownerName || !data.ownerEmail}
                onClick={() => setStep(3)}
                style={btnPrimary(!data.ownerName || !data.ownerEmail)}>
                Далее →
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: FONT.display, fontSize: 24, fontWeight: 700, marginBottom: 8, color: C.text }}>
              Тарифный план <Gem size={22} />
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>
              Шаг 3 из 3 — выбери план и срок trial
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
              {PLANS.map((p) => (
                <button key={p.id} onClick={() => setData({ ...data, plan: p.id })} style={{
                  background: data.plan === p.id ? C.brandSoft : C.bgCard,
                  border: data.plan === p.id ? `2px solid ${C.brand}` : `1px solid ${C.border}`,
                  borderRadius: 12, padding: 16,
                  cursor: "pointer", textAlign: "left", position: "relative",
                }}>
                  {p.recommend && (
                    <div style={{
                      position: "absolute", top: -8, right: 16,
                      background: C.brand, color: "#0A0E14",
                      fontSize: 9, fontWeight: 800, letterSpacing: 1,
                      padding: "2px 8px", borderRadius: 100,
                    }}>POPULAR</div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                    <div style={{ fontFamily: FONT.display, fontSize: 18, fontWeight: 700, color: C.text }}>
                      {p.label}
                    </div>
                    <div style={{ fontFamily: FONT.display, fontSize: 18, fontWeight: 700, color: C.brand }}>
                      ${p.price}/мес
                    </div>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4, fontSize: 11, color: C.muted }}>
                    {p.features.map((f, i) => (
                      <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Check size={12} /> {f}{i < p.features.length - 1 && <span style={{ margin: "0 2px" }}>·</span>}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
            <FormField label="Длительность trial">
              <div style={{ display: "flex", gap: 8 }}>
                {([7, 14, 30] as const).map((d) => (
                  <button key={d} onClick={() => setData({ ...data, trialDays: d })} style={{
                    flex: 1, padding: "10px",
                    background: data.trialDays === d ? C.brandSoft : C.bgCard,
                    color: data.trialDays === d ? C.brand : C.text,
                    border: `1px solid ${data.trialDays === d ? C.brand : C.border}`,
                    borderRadius: 10, cursor: "pointer",
                    fontSize: 12, fontWeight: 700,
                  }}>{d} дней</button>
                ))}
              </div>
            </FormField>
            {submitError && (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 6,
                marginTop: 16, padding: 12,
                background: C.redSoft, border: `1px solid ${C.red}40`,
                borderRadius: 10, color: C.red, fontSize: 12,
              }}>
                <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{submitError}</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 32 }}>
              <button onClick={() => setStep(2)} style={btnSecondary()}>← Назад</button>
              <button onClick={() => void handleSubmit()} disabled={submitting}
                style={{ ...btnPrimary(submitting), display: "inline-flex", alignItems: "center", gap: 6 }}>
                {submitting ? "Создаём..." : <><Check size={16} /> Создать кафе</>}
              </button>
            </div>
          </>
        )}

        {step === 4 && submitResult && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{
              width: 80, height: 80, borderRadius: "50%",
              background: `linear-gradient(135deg, ${C.green}, ${C.brand})`,
              color: "white", display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 24px",
              boxShadow: `0 12px 32px ${C.green}40`,
            }}><Check size={40} /></div>
            <div style={{ fontFamily: FONT.display, fontSize: 28, fontWeight: 700, marginBottom: 8, color: C.text }}>
              Кафе создано!
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>
              {data.cafeName} ({data.city}) добавлено на платформу
            </div>
            {submitResult.leadConverted && (
              <div style={{
                background: C.greenSoft, border: `1px solid ${C.green}40`,
                borderRadius: 10, padding: 12, marginBottom: 16,
                color: C.green, fontSize: 12, textAlign: "left",
              }}>
                🎉 Лид сконвертирован в платящий tenant. Стадия →{" "}
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3, verticalAlign: "middle" }}>
                  <CheckCircle2 size={13} /> Платят
                </span>.
                {" "}Telegram-уведомление отправлено.
              </div>
            )}
            {submitResult.leadConvertError && (
              <div style={{
                background: C.orangeSoft, border: `1px solid ${C.orange}40`,
                borderRadius: 10, padding: 12, marginBottom: 16,
                color: C.orange, fontSize: 12, textAlign: "left",
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>Кафе создано, но привязка лида упала: {submitResult.leadConvertError}</span>
                </div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
                  Свяжите вручную через CRM → лид → "Уже есть кафе".
                </div>
              </div>
            )}
            {/* Статус авто-регистрации домена в Vercel. Если manual-add-required —
                super-admin должен добавить домен вручную, иначе URL кафе молча
                не открывается (SSL не выпускается без домена в project). */}
            {submitResult.vercelDomain === "manual-add-required" && (
              <div style={{
                background: C.orangeSoft, border: `1px solid ${C.orange}40`,
                borderRadius: 10, padding: 12, marginBottom: 16,
                color: C.orange, fontSize: 12, textAlign: "left",
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>Домен <strong>{submitResult.tenantSlug}.e-menu.bar</strong> не
                  добавился в Vercel автоматически.</span>
                </div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
                  Добавь вручную: Vercel → проект, обслуживающий e-menu.bar → Domains → Add →
                  {" "}{submitResult.tenantSlug}.e-menu.bar. Без этого URL кафе не
                  откроется (SSL-сертификат не выпустится).
                  {submitResult.vercelError ? ` Причина: ${submitResult.vercelError}` : ""}
                </div>
              </div>
            )}
            {submitResult.vercelDomain === "auto-added" && (
              <div style={{
                background: C.greenSoft, border: `1px solid ${C.green}40`,
                borderRadius: 10, padding: 12, marginBottom: 16,
                color: C.green, fontSize: 12, textAlign: "left",
                display: "flex", alignItems: "flex-start", gap: 6,
              }}>
                <CheckCircle2 size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Домен {submitResult.tenantSlug}.e-menu.bar зарегистрирован в Vercel —
                URL заработает после выпуска SSL (обычно &lt;1 мин).</span>
              </div>
            )}
            {submitResult.orgId && (
              <div style={{
                background: C.greenSoft, border: `1px solid ${C.green}40`,
                borderRadius: 10, padding: 12, marginBottom: 16,
                color: C.green, fontSize: 12, textAlign: "left",
                display: "flex", alignItems: "flex-start", gap: 6,
              }}>
                <Globe size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Кафе привязано к сети «{submitResult.orgId}» — владелец увидит его
                на экране «Сеть» в админке.</span>
              </div>
            )}
            {submitResult.planId && (
              <div style={{
                background: C.greenSoft, border: `1px solid ${C.green}40`,
                borderRadius: 10, padding: 12, marginBottom: 16,
                color: C.green, fontSize: 12, textAlign: "left",
                display: "flex", alignItems: "flex-start", gap: 6,
              }}>
                <Gem size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Подписка создана: план «{submitResult.planId}», trial {data.trialDays} дней.</span>
              </div>
            )}
            {submitResult.planError && (
              <div style={{
                background: C.redSoft, border: "1px solid rgba(239,68,68,0.4)",
                borderRadius: 10, padding: 12, marginBottom: 16,
                color: "#ef4444", fontSize: 12, textAlign: "left",
                display: "flex", alignItems: "flex-start", gap: 6,
              }}>
                <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Кафе создано, но подписка не назначена: {submitResult.planError}.
                Назначь план в карточке кафе.</span>
              </div>
            )}
            {submitResult.orgError && (
              <div style={{
                background: C.redSoft ?? "rgba(220,53,69,0.08)", border: "1px solid rgba(220,53,69,0.4)",
                borderRadius: 10, padding: 12, marginBottom: 16,
                color: "#dc3545", fontSize: 12, textAlign: "left",
                display: "flex", alignItems: "flex-start", gap: 6,
              }}>
                <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Кафе создано, но привязка к сети не удалась: {submitResult.orgError}.
                Привяжи вручную (tenants.org_id) или повтори через SQL/MCP.</span>
              </div>
            )}
            <div style={{
              background: C.bgSoft, border: `1px solid ${C.border}`,
              borderRadius: 12, padding: 20, marginBottom: 24, textAlign: "left",
            }}>
              <div style={{
                fontSize: 11, color: C.muted, letterSpacing: 1,
                textTransform: "uppercase", fontWeight: 700, marginBottom: 12,
              }}>Данные для входа владельца</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Row label="Slug" value={submitResult.tenantSlug} mono brand />
                <Row label="URL" value={submitResult.ownerSetupUrl} mono brand />
                <Row label="Email" value={data.ownerEmail} mono />
                <Row label="План" value={PLANS.find((p) => p.id === data.plan)?.label ?? data.plan} />
                <Row label="Trial" value={`${data.trialDays} дней`} orange />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => navigator.clipboard?.writeText(submitResult.ownerSetupUrl)} style={{
                flex: 1, padding: "12px",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                background: C.bgSoft, color: C.text,
                border: `1px solid ${C.border}`, borderRadius: 10,
                fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}><Clipboard size={14} /> Скопировать URL</button>
              <button onClick={() => window.open(submitResult.ownerSetupUrl, "_blank")} style={{
                flex: 1, padding: "12px",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                background: C.bgSoft, color: C.text,
                border: `1px solid ${C.border}`, borderRadius: 10,
                fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}><ExternalLink size={14} /> Открыть</button>
            </div>
            <button onClick={onClose} style={{
              marginTop: 16, width: "100%",
              background: C.brand, color: "#0A0E14", border: "none",
              padding: "14px", borderRadius: 10,
              fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}>Готово</button>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
}

function Row({ label, value, mono, brand, orange }: {
  label: string; value: string; mono?: boolean; brand?: boolean; orange?: boolean;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
      <span style={{ fontSize: 12, color: C.muted }}>{label}:</span>
      <span style={{
        fontSize: 12, fontWeight: 700,
        fontFamily: mono ? FONT.mono : FONT.body,
        color: brand ? C.brand : orange ? C.orange : C.text,
        wordBreak: "break-all", textAlign: "right",
      }}>{value}</span>
    </div>
  );
}
